import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  makeStyles,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Modal,
  Theme,
  createStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import {
  ShopLocalCampaignInput,
  ShopLocalCampaignParticipatingMembersInput,
  useUpdateShopLocalCampaignMutation,
  useGetShopLocalParticipatingMemberEligibilityQuery,
  UserType,
  useGetShopLocalCampaignQuery,
  useEndShopLocalCampaignMutation,
  GetAllShopLocalCampaignsForChamberDocument,
} from "../../../graphql/types";
import "react-toastify/dist/ReactToastify.css";
import { useFormik } from "formik";
import * as yup from "yup";
import dayjs from "dayjs";
import useRedirect from "../../common/Hooks/useRedirect";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import ShopLocalCampaignEndDatePicker from "./ShopLocalCampaignEndDatePicker";
import Loader from "../../common/Loader/Loader";
import ChooseParticipatingMembers from "./ChooseParticipatingMembers";
import { normalizeShopLocalPrizeDescription } from "./shopLocalNotification";
import { useLocation } from "react-router-dom";
import omitDeep from "omit-deep-lodash";
import ExportButton from "../../common/ExportButton";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ContestNotificationMockup from "../../../images/Shop Local Mockup Contest Notification.svg";
import ContestRulesMockup from "../../../images/Mockup for Contest Prize.svg";
import SuccessIcon from "../../../images/success.png";
import InfoIcon from "../../../images/info.png";

const CHARACTER_LIMIT_DESCRIPTION = 250;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      background: "#E1A731",
      minHeight: "100vh",
      fontFamily: "DM Sans, sans-serif",
      overflowX: "hidden" as const,
    },
    header: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      padding: "30px 40px",
      gap: "8px",
      background: "#E1A731",
      [theme.breakpoints.down("sm")]: {
        padding: "20px 16px",
      },
    },
    pageTitle: {
      fontFamily: "DM Sans, sans-serif",
      fontStyle: "normal",
      fontWeight: "bold",
      fontSize: "32px",
      lineHeight: "42px",
      color: "white",
      margin: 0,
      [theme.breakpoints.down("sm")]: {
        fontSize: "22px",
        lineHeight: "30px",
      },
    },
    pageSubtitle: {
      fontFamily: "DM Sans, sans-serif",
      fontStyle: "normal",
      fontWeight: 400,
      fontSize: "18px",
      lineHeight: "23px",
      color: "rgba(255, 255, 255, 0.9)",
      marginTop: "8px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "16px",
        lineHeight: "1.55",
      },
    },
    contentContainer: {
      padding: "0 40px 40px 40px",
      background: "#E1A731",
      [theme.breakpoints.down("sm")]: {
        padding: "0 12px 24px 12px",
      },
    },
    contentCard: {
      background: "#FFFFFF",
      borderRadius: "20px",
      padding: "30px 41px",
      width: "100%",
      minHeight: "600px",
      boxSizing: "border-box" as const,
      [theme.breakpoints.down("sm")]: {
        padding: "16px",
        borderRadius: "14px",
        minHeight: 0,
      },
    },
    headerButtonRow: {
      display: "flex",
      gap: "15px",
      flexWrap: "wrap",
      marginTop: "20px",
    },
    setupGuidePill: {
      position: "absolute" as const,
      top: "-22px",
      left: "60px",
      display: "inline-flex",
      backgroundColor: "#E1A731",
      color: "#FFFFFF",
      fontWeight: 700,
      fontSize: "16px",
      borderRadius: "999px",
      padding: "12px 32px",
      letterSpacing: "0.02em",
      whiteSpace: "nowrap" as const,
      [theme.breakpoints.down("sm")]: {
        top: "-18px",
        left: "20px",
        fontSize: "13px",
        padding: "9px 22px",
      },
    },
    formFrame: {
      position: "relative" as const,
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      padding: "60px 51px 49px",
      gap: "10px",
      width: "900px",
      maxWidth: "100%",
      background: "#FFFFFF",
      border: "0.7px solid #888888",
      borderRadius: "31px",
      margin: "20px auto 0",
      boxSizing: "border-box" as const,
      [theme.breakpoints.down("sm")]: {
        padding: "44px 16px 24px",
        width: "100%",
        borderRadius: "16px",
      },
    },
    form: {
      width: "100%",
    },
    fieldContainer: {
      marginBottom: "28px",
      "&:last-child": {
        marginBottom: 0,
      },
    },
    fieldLabelRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "6px",
    },
    fieldLabel: {
      fontSize: "16px",
      fontWeight: 600,
      color: "#000000",
      display: "flex",
      alignItems: "center",
      fontFamily: "DM Sans, sans-serif",
    },
    subFieldLabel: {
      fontSize: "15px",
      fontWeight: 700,
      color: "#000000",
      display: "flex",
      alignItems: "center",
      fontFamily: "DM Sans, sans-serif",
      marginBottom: "10px",
    },
    requiredAsterisk: {
      color: "#D32F2F",
      marginLeft: "4px",
      fontWeight: "bold",
    },
    helperText: {
      fontSize: "16px",
      color: "#000000",
      marginBottom: "12px",
      lineHeight: "1.5",
      fontFamily: "DM Sans, sans-serif",
    },
    inputField: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        backgroundColor: "#FFFFFF",
        "& fieldset": {
          borderColor: "#000000",
        },
        "&:hover fieldset": {
          borderColor: "#000000",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#E1A731",
          borderWidth: "2px",
        },
        "&.Mui-error fieldset": {
          borderColor: "#DD3730",
        },
      },
      "& .MuiInputBase-input": {
        padding: "14px 16px",
        fontSize: "14px",
      },
      "& .MuiInputBase-inputMultiline": {
        padding: "14px 16px",
      },
    },
    inputFieldError: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "#E1A731",
          borderWidth: "2px",
        },
      },
    },
    prizeFieldBox: {
      width: "100%",
      minHeight: "200px",
      border: "1px solid #000000",
      borderRadius: "8px",
      padding: "20px 22px",
      backgroundColor: "#FFFFFF",
      fontFamily: "DM Sans, sans-serif",
      fontSize: "15px",
      lineHeight: 1.6,
      color: "#000000",
      cursor: "text",
      "&:focus-within": {
        borderColor: "#E1A731",
        borderWidth: "2px",
        padding: "19px 21px",
      },
    },
    prizeFieldBoxError: {
      borderColor: "#DD3730 !important",
      borderWidth: "2px !important",
      padding: "13px 15px !important",
    },
    prizeFieldInput: {
      border: "none",
      outline: "none",
      background: "transparent",
      padding: 0,
      margin: 0,
      fontFamily: "DM Sans, sans-serif",
      fontSize: "15px",
      lineHeight: 1.6,
      color: "#000000",
      minWidth: "180px",
      width: "100%",
      flex: 1,
      resize: "none" as const,
      verticalAlign: "top" as const,
      "&::placeholder": { color: "#9CA3AF" },
    },
    errorContainer: {
      display: "flex",
      alignItems: "center",
      marginTop: "6px",
    },
    errorBadge: {
      background: "#FCEBE9",
      border: "1px dashed #DD3730",
      borderRadius: "7px",
      padding: "4px 10px",
      fontFamily: "DM Sans, sans-serif",
      fontStyle: "normal",
      fontWeight: 500,
      fontSize: "11px",
      lineHeight: "15px",
      color: "#DD3730",
    },
    charCounter: {
      fontSize: "13px",
      color: "#555555",
      textAlign: "left" as const,
      marginTop: "6px",
    },
    previewSection: {
      marginTop: "32px",
      marginBottom: "36px",
    },
    previewSectionTitle: {
      fontSize: "16px",
      fontWeight: 700,
      color: "#000000",
      margin: "0 0 16px 0",
      lineHeight: "22px",
      fontFamily: "DM Sans, sans-serif",
    },
    previewButtonRow: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "14px",
      alignItems: "flex-start",
    },
    previewBlackBtn: {
      backgroundColor: "#24272C",
      color: "#FFFFFF",
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 700,
      fontSize: "15px",
      borderRadius: "999px",
      padding: "12px 36px",
      textTransform: "none" as const,
      boxShadow: "none",
      width: "320px",
      maxWidth: "100%",
      "&:hover": { backgroundColor: "#000000", boxShadow: "none" },
    },
    dateFieldsContainer: {
      display: "flex",
      gap: "20px",
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
      },
    },
    dateFieldWrapper: {
      flex: 1,
    },
    actionsRow: {
      display: "flex",
      gap: "20px",
      justifyContent: "center",
      marginTop: "40px",
      flexWrap: "wrap",
    },
    submitButton: {
      background: "#E1A731",
      color: "white",
      borderRadius: "999px",
      padding: "14px 80px",
      fontSize: "16px",
      fontWeight: 700,
      textTransform: "none",
      minWidth: "260px",
      letterSpacing: "0.06em",
      boxShadow: "none",
      "&:hover": {
        background: "#c99228",
        boxShadow: "none",
      },
      "&:disabled": {
        background: "#ccc",
      },
    },
    endContestButton: {
      background: "#E53935",
      color: "white",
      borderRadius: "999px",
      padding: "14px 80px",
      fontSize: "16px",
      fontWeight: 700,
      textTransform: "none",
      minWidth: "260px",
      letterSpacing: "0.06em",
      boxShadow: "none",
      "&:hover": {
        background: "#C71920",
        boxShadow: "none",
      },
      "&:disabled": {
        background: "#ccc",
      },
    },
    invalidError: {
      textAlign: "center",
      color: "#DD3730",
      fontSize: "14px",
      marginTop: "15px",
    },
    modal: {
      border: "unset",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    paper: {
      height: "600px",
      borderRadius: "15px",
      textAlign: "center",
      width: "740px",
      maxWidth: "90%",
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      backgroundColor: "white",
      padding: "40px",
      position: "relative",
      boxSizing: "border-box" as const,
      [theme.breakpoints.down("sm")]: {
        maxWidth: "calc(100% - 24px)",
        maxHeight: "calc(100vh - 24px)",
        height: "auto",
        padding: "24px 18px",
        borderRadius: "14px",
      },
    },
    modalButton: {
      margin: "10px",
      marginTop: "20px",
      width: "230px",
      backgroundColor: "#E1A731",
      color: "white",
      fontWeight: "bold",
      height: "45px",
      borderRadius: "25px",
      "&:hover": {
        backgroundColor: "#c99228",
      },
    },
    modalSecondaryButton: {
      backgroundColor: "#24272C",
      color: "#FFFFFF",
      "&:hover": { backgroundColor: "#000" },
    },
    successPaper: {
      position: "relative" as const,
      backgroundColor: "white",
      borderRadius: "16px",
      width: "560px",
      maxWidth: "calc(100vw - 24px)",
      maxHeight: "calc(100vh - 24px)",
      padding: "40px 40px 32px",
      boxSizing: "border-box" as const,
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      outline: "none",
      overflowY: "auto" as const,
      [theme.breakpoints.down("sm")]: {
        width: "calc(100vw - 24px)",
        padding: "32px 20px 24px",
        borderRadius: "14px",
      },
    },
    successCloseBtn: {
      position: "absolute" as const,
      top: "14px",
      right: "14px",
      padding: 0,
      width: "30px",
      height: "30px",
      backgroundColor: "#DD3730",
      color: "#FFFFFF",
      "&:hover": { backgroundColor: "#B92E26" },
    },
    successCheckCircle: {
      width: "72px",
      height: "72px",
      borderRadius: "50%",
      backgroundColor: "#00B621",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "20px",
      border: "3px solid #FFFFFF",
      boxShadow: "0 0 0 3px #00B621",
    },
    successHeading: {
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 700,
      fontSize: "22px",
      color: "#000000",
      margin: "0 0 24px 0",
      textAlign: "center" as const,
      [theme.breakpoints.down("sm")]: { fontSize: "18px" },
    },
    successBody: {
      fontFamily: "DM Sans, sans-serif",
      fontSize: "15px",
      color: "#000000",
      lineHeight: 1.5,
      margin: "0 0 18px 0",
      textAlign: "left" as const,
      [theme.breakpoints.down("sm")]: { fontSize: "14px" },
      "&:last-child": { marginBottom: 0 },
    },
    previewPaper: {
      position: "relative" as const,
      backgroundColor: "white",
      borderRadius: "15px",
      width: "360px",
      maxWidth: "calc(100vw - 24px)",
      maxHeight: "calc(100vh - 24px)",
      padding: "32px 20px 24px",
      boxSizing: "border-box" as const,
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      outline: "none",
      [theme.breakpoints.down("sm")]: {
        width: "300px",
        padding: "30px 16px 20px",
        borderRadius: "12px",
      },
    },
    previewCloseBtn: {
      position: "absolute" as const,
      top: "10px",
      right: "10px",
      padding: 0,
      width: "28px",
      height: "28px",
      backgroundColor: "#DD3730",
      color: "#FFFFFF",
      "&:hover": {
        backgroundColor: "#B92E26",
      },
    },
    previewTitle: {
      fontWeight: 700,
      color: "#000000",
      margin: "0 0 8px 0",
      fontSize: "18px",
      textAlign: "center" as const,
      [theme.breakpoints.down("sm")]: {
        fontSize: "16px",
      },
    },
    mockupFrame: {
      position: "relative" as const,
      width: "240px",
      maxWidth: "100%",
      aspectRatio: "648 / 1278",
      margin: "4px auto 0",
      [theme.breakpoints.down("sm")]: {
        width: "200px",
      },
    },
    mockupImage: {
      width: "100%",
      height: "100%",
      display: "block",
    },
    notificationTitleOverlay: {
      position: "absolute" as const,
      top: "42%",
      left: "26%",
      right: "12%",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif",
      fontSize: "11px",
      fontWeight: 700,
      lineHeight: 1.2,
      color: "#000000",
      textAlign: "left" as const,
      whiteSpace: "nowrap" as const,
      overflow: "hidden",
      textOverflow: "ellipsis",
      [theme.breakpoints.down("sm")]: {
        fontSize: "10px",
      },
    },
    rulesMockupFrame: {
      position: "relative" as const,
      width: "260px",
      maxWidth: "100%",
      aspectRatio: "789 / 1597",
      margin: "4px auto 0",
      [theme.breakpoints.down("sm")]: {
        width: "220px",
      },
    },
    rulesOverlay: {
      position: "absolute" as const,
      top: "44%",
      left: "8%",
      right: "8%",
      bottom: "11%",
      display: "flex",
      flexDirection: "column" as const,
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif",
      color: "#000000",
      overflow: "hidden",
    },
    rulesTitle: {
      fontWeight: 700,
      fontSize: "12px",
      textAlign: "center" as const,
      margin: "0 0 6px 0",
      [theme.breakpoints.down("sm")]: { fontSize: "11px" },
    },
    rulesIntro: {
      fontSize: "10px",
      fontWeight: 700,
      margin: "0 0 4px 0",
      lineHeight: 1.3,
      [theme.breakpoints.down("sm")]: { fontSize: "9px" },
    },
    rulesList: {
      fontSize: "9.5px",
      lineHeight: 1.3,
      margin: 0,
      padding: "0 0 0 12px",
      "& li": { marginBottom: "3px" },
      [theme.breakpoints.down("sm")]: { fontSize: "8.5px" },
    },
    rulesFooter: {
      fontSize: "9px",
      lineHeight: 1.3,
      marginTop: "10px",
      paddingTop: "4px",
      [theme.breakpoints.down("sm")]: { fontSize: "8px" },
    },
    endModalPaper: {
      position: "relative" as const,
      backgroundColor: "#FFFFFF",
      borderRadius: "16px",
      width: "640px",
      maxWidth: "calc(100vw - 24px)",
      maxHeight: "calc(100vh - 24px)",
      padding: "36px 40px 32px",
      boxSizing: "border-box" as const,
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      outline: "none",
      overflowY: "auto" as const,
      [theme.breakpoints.down("sm")]: {
        width: "calc(100vw - 24px)",
        padding: "28px 18px 22px",
        borderRadius: "14px",
      },
    },
    endModalCloseBtn: {
      position: "absolute" as const,
      top: "14px",
      right: "14px",
      padding: 0,
      width: "30px",
      height: "30px",
      backgroundColor: "#DD3730",
      color: "#FFFFFF",
      "&:hover": { backgroundColor: "#B92E26" },
    },
    endModalIconWrap: {
      width: "92px",
      height: "92px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "20px",
      "& img": {
        width: "92px",
        height: "92px",
        display: "block",
      },
    },
    endModalTitle: {
      fontFamily: "DM Sans, sans-serif",
      fontWeight: 700,
      fontSize: "22px",
      color: "#000000",
      margin: "0 0 18px 0",
      textAlign: "center" as const,
      [theme.breakpoints.down("sm")]: { fontSize: "18px" },
    },
    endModalBody: {
      fontFamily: "DM Sans, sans-serif",
      fontSize: "15px",
      color: "#000000",
      lineHeight: 1.55,
      margin: "0 0 14px 0",
      textAlign: "center" as const,
      maxWidth: "520px",
      [theme.breakpoints.down("sm")]: { fontSize: "14px" },
    },
    endModalActions: {
      display: "flex",
      gap: "24px",
      marginTop: "10px",
      flexWrap: "wrap" as const,
      justifyContent: "center",
    },
    endModalGoldBtn: {
      backgroundColor: "#E1A731",
      color: "#FFFFFF",
      fontWeight: 700,
      fontSize: "14px",
      letterSpacing: "0.04em",
      borderRadius: "999px",
      padding: "12px 38px",
      minWidth: "200px",
      textTransform: "uppercase" as const,
      boxShadow: "none",
      "&:hover": { backgroundColor: "#c99228", boxShadow: "none" },
      "&:disabled": { backgroundColor: "#E5C76A", color: "#FFFFFF" },
    },
    endModalDarkBtn: {
      backgroundColor: "#24272C",
      color: "#FFFFFF",
      fontWeight: 700,
      fontSize: "14px",
      letterSpacing: "0.04em",
      borderRadius: "999px",
      padding: "12px 38px",
      minWidth: "200px",
      textTransform: "uppercase" as const,
      boxShadow: "none",
      "&:hover": { backgroundColor: "#000", boxShadow: "none" },
      "&:disabled": { backgroundColor: "#888", color: "#FFFFFF" },
    },
    notificationBodyOverlay: {
      position: "absolute" as const,
      top: "45.2%",
      left: "26%",
      right: "12%",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif",
      fontSize: "9px",
      lineHeight: 1.25,
      color: "#000000",
      textAlign: "left" as const,
      wordBreak: "break-word" as const,
      display: "-webkit-box",
      WebkitBoxOrient: "vertical" as const,
      WebkitLineClamp: 4,
      overflow: "hidden",
      [theme.breakpoints.down("sm")]: {
        fontSize: "8px",
      },
    },
  })
);

const UpdateChamberShopLocalCampaign = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  const [loader, setLoader] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [customError, setCustomError] = useState(
    enumStrings.userFacingErrors.saveFailedStaff
  );
  const [invalidError, setInvalidError] = useState(false);
  const [updateShopLocalCampaign] = useUpdateShopLocalCampaignMutation();
  const redirectToShopLocalCampaignList = useRedirect(
    RedirectUri.activeChamberShopLocal
  );

  const chamberInfo = useGetChamberFromUserIdInAuth();

  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [endCampaignModalOpen, setEndCampaignModalOpen] = useState(false);
  const [notificationPreviewOpen, setNotificationPreviewOpen] = useState(false);
  const [descriptionPreviewOpen, setDescriptionPreviewOpen] = useState(false);

  const location = useLocation();
  const shopLocalCampaignId = useMemo(() => {
    return (location.state as { shopLocalCampaignId: string })
      ?.shopLocalCampaignId;
  }, [location.state]);

  const { data: shopLocalCampaignInfo } = useGetShopLocalCampaignQuery({
    variables: {
      id: shopLocalCampaignId,
    },
    skip: !shopLocalCampaignId,
  });

  const intitalValuesMemo = useMemo(() => {
    const dataObj = shopLocalCampaignInfo?.getShopLocalCampaign;
    const initialValues: ShopLocalCampaignInput = {
      startDateUtcMilli: dataObj?.startDateUtcMilli || 0,
      endDateUtcMilli: dataObj?.endDateUtcMilli || 0,
      participatingMembers: (dataObj?.participatingMembers || []).map(
        (member) => ({
          id: member.memberId,
        })
      ),
      campaignName: dataObj?.campaignName || "",
      prizeDescription: dataObj?.prizeDescription || "",
    };
    return omitDeep(initialValues, "__typename");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopLocalCampaignInfo?.getShopLocalCampaign]) as unknown as ShopLocalCampaignInput;

  const validationSchema = yup.object({
    campaignName: yup
      .string()
      .trim()
      .required(
        enumStrings.updateChamberShopLocalCompaignValidation.contestName
      ),
    prizeDescription: yup
      .string()
      .trim()
      .required(
        enumStrings.updateChamberShopLocalCompaignValidation.contestDescription
      ),
    participatingMembers: yup
      .array()
      .required()
      .min(
        1,
        enumStrings.updateChamberShopLocalCompaignValidation
          .oneParticipationPerMember
      )
      .required(
        enumStrings.updateChamberShopLocalCompaignValidation
          .participantIsRequired
      ),
    startDateUtcMilli: yup
      .number()
      .required(
        enumStrings.updateChamberShopLocalCompaignValidation.startDateUtcMilli
      )
      .min(
        0,
        enumStrings.updateChamberShopLocalCompaignValidation.startDateUtcMilli
      ),
    endDateUtcMilli: yup
      .number()
      .required(
        enumStrings.updateChamberShopLocalCompaignValidation.endDateUtcMilli
      )
      .min(
        dayjs().startOf("day").valueOf(),
        enumStrings.updateChamberShopLocalCompaignValidation.endDateUtcMilli
      ),
  });

  const formik = useFormik({
    initialValues: intitalValuesMemo,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (input: ShopLocalCampaignInput) => {
      setLoader(true);
      try {
        setButtonDisabled(true);

        if (input.startDateUtcMilli > input.endDateUtcMilli) {
          setInvalidError(true);
          setCustomError(enumStrings.shopLocal.shopLocalCampaign.customErrors);
          setLoader(false);
          setButtonDisabled(false);
          return;
        }

        const shopLocalCampaignCreation = await updateShopLocalCampaign({
          variables: {
            id: shopLocalCampaignId,
            input: {
              startDateUtcMilli: input.startDateUtcMilli,
              endDateUtcMilli: input.endDateUtcMilli,
              participatingMembers: input.participatingMembers.map(
                (member) => ({ id: member.id })
              ),
              campaignName: input.campaignName,
              prizeDescription: input.prizeDescription,
            },
          },
          refetchQueries: [
            { query: GetAllShopLocalCampaignsForChamberDocument },
            "getAllShopLocalCampaignsForChamber",
          ],
          awaitRefetchQueries: true,
        });
        const updationResult =
          shopLocalCampaignCreation.data?.updateShopLocalCampaign;
        if (updationResult?.shopLocalCampaignUpdatedSuccessfully === false) {
          setInvalidError(true);
          setCustomError(
            enumStrings.shopLocal.updateChamberShopLocalCampaign.invalidEntry
          );
          setLoader(false);
          setButtonDisabled(false);
          return;
        }

        setLoader(false);
        setUpdateModalOpen(true);
      } catch (error) {
        setInvalidError(true);
        setLoader(false);
        setButtonDisabled(false);
      }
    },
  });

  const notificationPreviewPrizeDescription =
    normalizeShopLocalPrizeDescription(formik.values.prizeDescription);

  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      const firstErrorField = document.querySelector(
        ".Mui-error, .MuiFormHelperText-root.Mui-error"
      );
      if (firstErrorField) {
        const container =
          firstErrorField.closest(".MuiFormControl-root") || firstErrorField;
        container.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.submitCount, formik.errors]);

  const updateChamberShopLocalCampaignObj = useMemo(() => {
    // @ts-ignore
    return enumStrings.groups[UserType.ChamberAdmin][
      RedirectUri.updateShopLocal
    ];
  }, [enumStrings.groups]);

  const contestDatesAreValid =
    formik.values.startDateUtcMilli > 0 &&
    formik.values.endDateUtcMilli > formik.values.startDateUtcMilli;
  const {
    data: participatingMemberEligibilityData,
    loading: participatingMemberEligibilityLoading,
    error: participatingMemberEligibilityError,
  } = useGetShopLocalParticipatingMemberEligibilityQuery({
    variables: {
      startDateUtcMilli: formik.values.startDateUtcMilli,
      endDateUtcMilli: formik.values.endDateUtcMilli,
    },
    skip: !chamberInfo?.id || !contestDatesAreValid,
    fetchPolicy: "cache-and-network",
  });
  const showLoader =
    !chamberInfo?.id || !shopLocalCampaignInfo?.getShopLocalCampaign;

  const handleChamberMembersChange = useCallback(
    (selectedChamberMembers: ShopLocalCampaignParticipatingMembersInput[]) => {
      formik.setFieldValue("participatingMembers", selectedChamberMembers);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className={classes.root}>
      {/* Contest Notification Preview Modal */}
      {notificationPreviewOpen && (
        <Modal
          className={classes.modal}
          open={notificationPreviewOpen}
          onClose={() => setNotificationPreviewOpen(false)}
        >
          <div className={classes.previewPaper}>
            <IconButton
              className={classes.previewCloseBtn}
              onClick={() => setNotificationPreviewOpen(false)}
              size="small"
              aria-label="close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <h2 className={classes.previewTitle}>
              Contest Notification Preview
            </h2>
            <div className={classes.mockupFrame}>
              <img
                src={ContestNotificationMockup}
                alt="Contest notification mockup"
                className={classes.mockupImage}
              />
              <div className={classes.notificationTitleOverlay}>
                Shop Local Contest
              </div>
              <div className={classes.notificationBodyOverlay}>
                {chamberInfo?.name
                  ? `${chamberInfo.name}'s shop local contest is now live. Tap to learn how to win `
                  : "Your shop local contest is now live. Tap to learn how to win "}
                {notificationPreviewPrizeDescription.length > 0
                  ? notificationPreviewPrizeDescription
                  : enumStrings.shopLocal.createShopLocalCampaign.enterText}
                .
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Contest Description Preview Modal */}
      {descriptionPreviewOpen && (
        <Modal
          className={classes.modal}
          open={descriptionPreviewOpen}
          onClose={() => setDescriptionPreviewOpen(false)}
        >
          <div className={classes.previewPaper}>
            <IconButton
              className={classes.previewCloseBtn}
              onClick={() => setDescriptionPreviewOpen(false)}
              size="small"
              aria-label="close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <h2 className={classes.previewTitle}>
              Contest Pop up Details/Rules Preview
            </h2>
            <div className={classes.rulesMockupFrame}>
              <img
                src={ContestRulesMockup}
                alt="Contest rules mockup"
                className={classes.mockupImage}
              />
              <div className={classes.rulesOverlay}>
                <h3 className={classes.rulesTitle}>Shop Local Contest Rules</h3>
                <p className={classes.rulesIntro}>
                  For a chance to win{" "}
                  {notificationPreviewPrizeDescription.length > 0
                    ? notificationPreviewPrizeDescription
                    : enumStrings.shopLocal.createShopLocalCampaign.enterText}
                  :
                </p>
                <ol className={classes.rulesList}>
                  <li>
                    Find participating companies using the{" "}
                    <strong>Shop Local badge</strong> in the app.
                  </li>
                  <li>
                    <strong>Visit</strong> a participating company to receive
                    their participating code to earn a ballot.
                  </li>
                  <li>
                    While there, tap on the company's perk, tap{" "}
                    <strong>Redeem</strong> and{" "}
                    <strong>ask participating company</strong> for their 4-digit
                    code, to be entered into the draw.
                  </li>
                </ol>
                <p className={classes.rulesFooter}>
                  By clicking "Agree," you consent to the{" "}
                  {chamberInfo?.name || "{INSERT CHAMBER NAME}"} accessing your
                  email{" "}
                  {
                    enumStrings.shopLocal.createShopLocalCampaign
                      .appUserEmailPreviewPlaceholder
                  }{" "}
                  and contact you if you win.
                </p>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Success Modal */}
      {updateModalOpen && (
        <Modal
          className={classes.modal}
          open={updateModalOpen}
          onClose={() => redirectToShopLocalCampaignList()}
        >
          <div className={classes.successPaper}>
            <IconButton
              className={classes.successCloseBtn}
              onClick={() => redirectToShopLocalCampaignList()}
              size="small"
              aria-label="close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <img
              src={SuccessIcon}
              alt=""
              style={{
                width: "72px",
                height: "72px",
                marginBottom: "20px",
              }}
            />
            <h2 className={classes.successHeading}>
              Contest Updated Successfully
            </h2>
            <p className={classes.successBody}>
              Participating members will receive a unique code in their Member
              Dashboard and will be highlighted in the app with a "Shop Local"
              badge. Your chamber can view each member's participation code by
              opening the list of participating members on the Active Contest
              page in your Chamber Dashboard.
            </p>
            <p className={classes.successBody}>
              App users must consent to share their email with your chamber to
              enter the contest. When they visit or redeem a perk (optional)
              from a participating company, they will be required to enter that
              company's unique code.
            </p>
            <p className={classes.successBody}>
              At the end of the campaign, a winner is randomly selected through
              your Chamber Dashboard and will be notified by email from your
              chamber.
            </p>
          </div>
        </Modal>
      )}

      {endCampaignModalOpen && (
        <EndCampaignModal
          campaignId={shopLocalCampaignId}
          closeModal={() => setEndCampaignModalOpen(false)}
        />
      )}

      <div className={classes.header}>
        <h1 className={classes.pageTitle}>
          {updateChamberShopLocalCampaignObj.title}
        </h1>
        <p className={classes.pageSubtitle}>
          Update any details that could affect the Shop Local Contest and are
          important for users to know.
        </p>
        <div className={classes.headerButtonRow}>
          <VideoTutorialButton
            label={
              enumStrings.shopLocal.updateChamberShopLocalCampaign.watchTutorial
            }
            tutorialUrl=""
          />
          <ExportButton
            customOnClick={() => undefined}
            disablePadding
            icon={
              <InfoOutlinedIcon
                style={{
                  color: "#E1A731",
                  marginRight: "10px",
                  width: "24px",
                  height: "24px",
                }}
              />
            }
            label={
              enumStrings.shopLocal.updateChamberShopLocalCampaign
                .viewInstructions
            }
          />
        </div>
      </div>

      <div className={classes.contentContainer}>
        <div className={classes.contentCard}>
          {showLoader ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                paddingTop: 20,
              }}
            >
              <Loader />
            </div>
          ) : (
            <div className={classes.formFrame}>
              <span className={classes.setupGuidePill}>
                {enumStrings.shopLocal.updateChamberShopLocalCampaign.setupGuide}
              </span>
              <form onSubmit={formik.handleSubmit} className={classes.form}>
                {/* Contest Name Section */}
                <div className={classes.fieldContainer}>
                  <div className={classes.fieldLabelRow}>
                    <div className={classes.fieldLabel}>
                      {enumStrings.chamberShopLocal.label.campaignName}
                      <span className={classes.requiredAsterisk}>*</span>
                    </div>
                  </div>
                  <p className={classes.helperText}>
                    {enumStrings.chamberShopLocal.tooltip.campaignName}
                  </p>
                  <TextField
                    variant="outlined"
                    className={classes.inputField}
                    placeholder={enumStrings.chamberShopLocal.label.campaignName}
                    name="campaignName"
                    value={formik.values.campaignName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.campaignName &&
                      Boolean(formik.errors.campaignName)
                    }
                    helperText={
                      formik.touched.campaignName && formik.errors.campaignName
                    }
                  />
                </div>

                {/* Prize Description Section */}
                <div className={classes.fieldContainer}>
                  <div className={classes.fieldLabelRow}>
                    <div className={classes.fieldLabel}>
                      Contest Prize Description
                      <span className={classes.requiredAsterisk}>*</span>
                    </div>
                  </div>
                  <p className={classes.helperText}>
                    Describe the prize that Chamber Perks App™ users can qualify
                    to win by participating in this Shop Local Contest. The
                    Description will be visible to users in the Chamber Perks App™.
                    {" "}
                    {
                      enumStrings.shopLocal.createShopLocalCampaign
                        .prizeDescriptionCompletePhraseHelper
                    }
                  </p>
                  <div
                    className={`${classes.prizeFieldBox} ${
                      formik.touched.prizeDescription &&
                      formik.errors.prizeDescription
                        ? classes.prizeFieldBoxError
                        : ""
                    }`}
                    onClick={() => {
                      const input = document.getElementById(
                        "prizeDescription-input"
                      );
                      input?.focus();
                    }}
                  >
                    <span>
                      {chamberInfo?.name || "{INSERT CHAMBER NAME}"}'s shop local
                      contest is now live. Tap to learn how to win{" "}
                    </span>
                    <textarea
                      id="prizeDescription-input"
                      className={classes.prizeFieldInput}
                      placeholder="Enter prize details here"
                      name="prizeDescription"
                      value={formik.values.prizeDescription}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      maxLength={CHARACTER_LIMIT_DESCRIPTION}
                      rows={4}
                    />
                  </div>
                  {formik.touched.prizeDescription &&
                    formik.errors.prizeDescription && (
                      <p
                        style={{
                          color: "#DD3730",
                          fontSize: "12px",
                          margin: "6px 0 0 0",
                        }}
                      >
                        {formik.errors.prizeDescription}
                      </p>
                    )}
                  <div className={classes.charCounter}>
                    {CHARACTER_LIMIT_DESCRIPTION -
                      (formik.values.prizeDescription || "").length}
                    {
                      enumStrings.shopLocal.createShopLocalCampaign
                        .charRemaining
                    }
                  </div>
                </div>

                {/* Preview Section */}
                <div className={classes.previewSection}>
                  <p className={classes.previewSectionTitle}>
                    Preview all shop local contest notifications and pop-ups as
                    they will appear in the app. {" "}
                    {
                      enumStrings.shopLocal.updateChamberShopLocalCampaign
                        .notificationUpdateDoesNotResend
                    }
                  </p>
                  <div className={classes.previewButtonRow}>
                    <Button
                      className={classes.previewBlackBtn}
                      onClick={() => setNotificationPreviewOpen(true)}
                      variant="contained"
                    >
                      Contest Notification
                    </Button>
                    <Button
                      className={classes.previewBlackBtn}
                      onClick={() => setDescriptionPreviewOpen(true)}
                      variant="contained"
                    >
                      Contest Pop up Details/Rules
                    </Button>
                  </div>
                </div>

                {/* Date Fields Section */}
                <div className={classes.fieldContainer}>
                  <div className={classes.fieldLabelRow}>
                    <div className={classes.fieldLabel}>
                      Contest Duration
                      <span className={classes.requiredAsterisk}>*</span>
                    </div>
                  </div>
                  <p className={classes.helperText}>
                    Select the start and end dates for your Shop Local Contest.
                    The contest will automatically begin and end at{" "}
                    <strong>12:00am</strong> on the selected dates.
                  </p>
                  <div className={classes.dateFieldsContainer}>
                    <div className={classes.dateFieldWrapper}>
                      <div className={classes.subFieldLabel}>
                        Contest Start Date
                        <span className={classes.requiredAsterisk}>*</span>
                      </div>
                      <ShopLocalCampaignEndDatePicker
                        formikErrors={formik.errors.startDateUtcMilli}
                        formikVal={formik.values.startDateUtcMilli}
                        formikSetFieldValue={formik.setFieldValue}
                        isForStartDate
                        fullWidth
                      />
                    </div>
                    <div className={classes.dateFieldWrapper}>
                      <div className={classes.subFieldLabel}>
                        Contest End Date
                        <span className={classes.requiredAsterisk}>*</span>
                      </div>
                      <ShopLocalCampaignEndDatePicker
                        formikErrors={formik.errors.endDateUtcMilli}
                        formikVal={formik.values.endDateUtcMilli}
                        formikSetFieldValue={formik.setFieldValue}
                        disabled={!(formik.values.startDateUtcMilli > 0)}
                        startAfterDate={formik.values.startDateUtcMilli}
                        fullWidth
                      />
                    </div>
                  </div>
                </div>

                {/* Participating Members Section */}
                <div className={classes.fieldContainer}>
                  <div className={classes.fieldLabelRow}>
                    <div className={classes.fieldLabel}>
                      Participating Members
                      <span className={classes.requiredAsterisk}>*</span>
                    </div>
                  </div>
                  <p className={classes.helperText}>
                    Select the member companies participating in this Shop Local
                    Contest.
                    <br />
                    Each participating member will be assigned a unique
                    participation code, which they can view in their Member
                    Dashboard under the Shop Local page.
                  </p>
                  <p className={classes.helperText}>
                    When a Chamber Perks App™ user redeems a perk from a
                    participating member, they will enter that member's
                    participation code in the app to be entered into the prize
                    draw.
                  </p>
                  <ChooseParticipatingMembers
                    selectedChamberMembers={formik.values.participatingMembers}
                    handleChamberMembersChange={handleChamberMembersChange}
                    eligibilityData={participatingMemberEligibilityData}
                    eligibilityLoading={participatingMemberEligibilityLoading}
                    eligibilityError={Boolean(participatingMemberEligibilityError)}
                    contestDatesAreValid={contestDatesAreValid}
                    formikErrorsAvailableToChamberIds={
                      formik.errors.participatingMembers
                    }
                  />
                </div>

                {/* Error Messages */}
                {invalidError && (
                  <div className={classes.invalidError}>{customError}</div>
                )}

                {Object.keys(formik?.errors || {}).length > 0 &&
                  Object.keys(formik?.touched || {}).length > 0 && (
                    <div className={classes.invalidError}>
                      {enumStrings.warningToCreate}
                    </div>
                  )}

                {/* Action buttons */}
                <div className={classes.actionsRow}>
                  <Button
                    type="submit"
                    className={classes.submitButton}
                    disabled={buttonDisabled || loader}
                    variant="contained"
                  >
                    {loader ? (
                      <CircularProgress size={24} style={{ color: "white" }} />
                    ) : (
                      enumStrings.shopLocal.updateChamberShopLocalCampaign
                        .updateButton
                    )}
                  </Button>
                  <Button
                    className={classes.endContestButton}
                    onClick={() => setEndCampaignModalOpen(true)}
                    disabled={buttonDisabled || loader}
                    variant="contained"
                  >
                    {enumStrings.shopLocal.updateChamberShopLocalCampaign
                      .endContest}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const EndCampaignModal = ({
  closeModal,
  campaignId,
}: {
  closeModal: () => void;
  campaignId: string;
}): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const [loader, setLoader] = useState(false);

  const [endCampaignExecution] = useEndShopLocalCampaignMutation();
  const redirectToShopLocalCampaignList = useRedirect(
    RedirectUri.chamberShopLocal,
    {
      showToast: true,
      toastText: enumStrings.shopLocal.shopLocalCampaign.toast,
    }
  );
  const endCampaign = useCallback(async () => {
    setLoader(true);
    await endCampaignExecution({
      variables: {
        id: campaignId,
      },
      refetchQueries: [
        { query: GetAllShopLocalCampaignsForChamberDocument },
        "getAllShopLocalCampaignsForChamber",
      ],
      awaitRefetchQueries: true,
    });
    closeModal();
    redirectToShopLocalCampaignList();
  }, [
    campaignId,
    closeModal,
    endCampaignExecution,
    redirectToShopLocalCampaignList,
  ]);

  return (
    <Modal className={classes.modal} open={true} onClose={closeModal}>
      <div className={classes.endModalPaper}>
        <IconButton
          className={classes.endModalCloseBtn}
          onClick={closeModal}
          size="small"
          aria-label="close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <div className={classes.endModalIconWrap}>
          <img src={InfoIcon} alt="" />
        </div>
        <h2 className={classes.endModalTitle}>End Shop Local Contest?</h2>
        <p className={classes.endModalBody}>
          Ending the contest will remove the contest from the Chamber Perks
          App™ and no additional Chamber Perks App™ users will be able to
          participate in the contest prize draw.
        </p>
        <p className={classes.endModalBody}>
          This contest cannot be restarted after ending.
        </p>
        <div className={classes.endModalActions}>
          <Button
            className={classes.endModalGoldBtn}
            onClick={() => {
              endCampaign();
            }}
            variant="contained"
            disabled={loader}
          >
            {loader ? (
              <CircularProgress size={22} style={{ color: "#fff" }} />
            ) : (
              "End Contest"
            )}
          </Button>
          <Button
            className={classes.endModalDarkBtn}
            onClick={closeModal}
            variant="contained"
            disabled={loader}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UpdateChamberShopLocalCampaign;
